import { useEffect, useState } from 'react';
import { getGhlWebsiteByDraft } from 'modules/api/digitalServices';

const DEFAULT_POLL_INTERVAL_MS = 2000;
const DEFAULT_POLL_TIMEOUT_MS = 20000;

type GhlWebsite = {
  ghlBookPageUrl?: string;
  subdomain?: string;
  subdomainFqdn?: string;
  [key: string]: unknown;
};

type Options = {
  // When false, the hook does the initial mount fetch but does NOT poll.
  // Useful when polling should only kick in after a publish lifecycle gate
  // (e.g. isDigitalBookReady on the View page).
  gateReady?: boolean;
  pollIntervalMs?: number;
  pollTimeoutMs?: number;
};

/**
 * Fetches and keeps the latest `ds__ghl_websites` record for a draft in sync.
 *
 * The DS upsert lags behind the Publish action because it is driven by a
 * BBA Kafka event consumer (`bba-site-ready.consumer.ts`). A single-shot
 * fetch right after Publish often returns 404 / a record without
 * `ghlBookPageUrl`, which previously caused the UI to show the legacy
 * Pagestead URL until the user reloaded the page.
 *
 * This hook closes that race by polling the endpoint until the record
 * carries `ghlBookPageUrl` or a timeout elapses. Callers gate the polling
 * with `gateReady` so it only runs when the publish flow has reached the
 * point at which BBA has emitted the event.
 */
const useGhlWebsite = (
  draftId: string | undefined,
  options: Options = {},
): GhlWebsite | null => {
  const {
    gateReady = true,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    pollTimeoutMs = DEFAULT_POLL_TIMEOUT_MS,
  } = options;

  const [ghlWebsite, setGhlWebsite] = useState<GhlWebsite | null>(null);

  // Initial fetch on mount / draftId change.
  useEffect(() => {
    if (!draftId) return undefined;
    let cancelled = false;
    getGhlWebsiteByDraft(draftId).then((data: GhlWebsite | null) => {
      if (!cancelled && data) setGhlWebsite(data);
    });
    return () => {
      cancelled = true;
    };
  }, [draftId]);

  // Poll while the gate is open and the URL is still missing.
  useEffect(() => {
    if (!draftId || !gateReady) return undefined;
    if (ghlWebsite?.ghlBookPageUrl) return undefined;

    let cancelled = false;
    let elapsed = 0;

    const intervalId = setInterval(() => {
      if (cancelled) return;
      elapsed += pollIntervalMs;
      getGhlWebsiteByDraft(draftId).then((data: GhlWebsite | null) => {
        if (cancelled) return;
        if (data) setGhlWebsite(data);
        if (data?.ghlBookPageUrl || elapsed >= pollTimeoutMs) {
          clearInterval(intervalId);
        }
      });
    }, pollIntervalMs);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [
    draftId,
    gateReady,
    ghlWebsite?.ghlBookPageUrl,
    pollIntervalMs,
    pollTimeoutMs,
  ]);

  return ghlWebsite;
};

export default useGhlWebsite;
